import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery as useApolloQuery, useMutation } from '@apollo/client';
import { IGetCarQuery } from '../../../services/carService/__generated__/queries.generated';
import { Modal } from '../../../components/modal';
import { URLS } from '../../../../constants/urls';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Discount from '../../../components/discount';
import PriceCalendar from '../../../components/PriceCalendar';
import TripPreferences from '../../../components/TripPreferences';
import { GET_CAR, UPDATE_CAR } from '../../../services/carService/queries';
import DistanceLimits from '../../../components/DistanceLimits';
import CarProtectionDeclinable from '../../../components/CarProtectionDeclinable';
import DepositAmount from '../../../components/DepositAmount';
import EditCarPhotos from '../../../components/EditCarPhotos';
import { IDiscount, ProductType, CarStatus } from '../../../../__generated__/graphql';
import AgeLimit from '../../../components/ageLimit';
import CarDetailsComponent from '../../../components/car/CarDetailsComponent';
import LoadingSpinner from '../../../components/LoadingSpinner';
import CarLocation from '../../../components/CarLocation';
import { toast } from 'react-toastify';
import { toastActions } from '../../../../store/toastStore';
import CarTripHistory from '../../../components/CarTripHistory';
import GuestInstructions from '../../../components/GuestInstructions';
import { DeliveryLocations } from './components/DeliveryLocations';
import MonthlyPricingSection from './components/MonthlyPricingSection';
import { useBrandRules, useHostCapabilities } from '../../../hooks/useHostCapabilities';
import { CAR_SECTION_CAPABILITY, carSectionView } from '../../../utils/hostCapabilities';
import { AdminOnlyNote, ManagedByBrandNote } from '../../../components/ManagedByBrandNote';
import { AdminOnlyBadge } from '../../../components/AdminOnlyBadge';
import { ADMIN_UNLIST_CAR, CAR_LISTING_STATUS } from '../../../services/listingHoldService/queries';
import { ICarListingStatusQuery } from '../../../services/listingHoldService/__generated__/queries.generated';
import { ListingHoldBanner } from './components/ListingHoldBanner';
import { getBrand } from '../../../constants/brands';
import { graphqlErrorMessage } from '../../../utils/listingHold';
import { Button } from '../../../components/button';
import { Field, Textarea } from '../../../components/ui';

/** Host car view sections temporarily hidden (re-enable when product-ready). */
const HIDDEN_HOST_CAR_SECTIONS = new Set(['displayName', 'extras']);

/**
 * Sidebar sections in order. Spec 014b: a car setting the site's brand sets is
 * hidden from hosts (its admin sees it, marked); the rest has no switch.
 */
const HOST_CAR_NAV: Array<[string, string]> = [
  ['dailyPricing', 'Daily Pricing'],
  ['hourlyPricing', 'Hourly Pricing'],
  ['monthlyPricing', 'Monthly pricing'],
  ['protectionPlan', 'Protection plan'],
  ['discounts', 'discounts'],
  ['ageLimit', 'Age Limit'],
  ['location', 'Location'],
  ['deliveryLocations', 'Delivery locations'],
  ['tripPreferences', 'Trip preferences'],
  ['distanceLimit', 'Distance limits'],
  ['depositAmount', 'Security deposit'],
  ['guestInstructions', 'Guest instructions'],
  ['photos', 'Photos'],
  ['details', 'Details'],
  ['tripHistory', 'Trip history'],
];

const PageContainer = styled.div`
  ${tw`w-full max-w-screen-xl min-w-0 box-border mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8`}
  ${tw`lg:flex lg:gap-10 lg:items-start`}
  min-height: calc(100vh - 68px);
`;

// Left Column
const LeftColumn = styled.aside`
  ${tw`w-full lg:w-[300px] lg:shrink-0`}
  @media (min-width: 1024px) {
    position: sticky;
    top: 24px;
  }
`;

const CarPreview = styled.div`
  ${tw`mb-6`}
`;

const CarImage = styled.div`
  ${tw`w-full rounded-card overflow-hidden mb-4 relative`}
  border: 1px solid var(--color-border);
  img {
    ${tw`w-full h-44 object-cover block`}
  }
`;

const ViewListingButton = styled.button`
  ${tw`
    absolute bottom-0 left-0
    w-full
    text-sm font-semibold
    py-2 px-4
    border-none
  `}
  color: var(--color-text-primary);
  background-color: rgb(255 255 255 / 0.85);
  backdrop-filter: blur(4px);

  &:hover {
    ${tw`cursor-pointer`}
    background-color: rgb(255 255 255 / 0.95);
  }
`;

const CarInfo = styled.div`
  ${tw`mb-6`}
`;

const CarTitle = styled.h2`
  ${tw`text-lg font-bold mb-0.5`}
  color: var(--color-text-primary);
`;

const CarDetails = styled.p`
  ${tw`text-text-secondary text-sm`}
`;

const NavigationList = styled.nav`
  ${tw`flex flex-col w-full gap-1 mb-6 max-w-full min-w-0 box-border`}
`;

const NavItem = styled.div<{ isActive?: boolean }>`
  ${tw`flex justify-between items-center py-2.5 px-3 rounded-lg cursor-pointer text-sm font-medium select-none`}
  color: ${({ isActive }) => (isActive ? 'var(--color-black)' : 'var(--color-text-secondary)')};
  background-color: ${({ isActive }) =>
    isActive ? 'rgb(var(--color-black-rgb) / 0.07)' : 'transparent'};
  transition:
    background-color var(--transition-normal),
    color var(--transition-normal);

  &:hover {
    background-color: ${({ isActive }) =>
      isActive ? 'rgb(var(--color-black-rgb) / 0.07)' : 'var(--color-bg-secondary)'};
    color: var(--color-text-primary);
  }
`;

const UpdatedBadge = styled.span`
  ${tw`ml-2 text-xs bg-blue-100 text-black px-2 py-0.5 rounded-full`}
`;

// Right Column
const RightColumn = styled.div`
  ${tw`hidden lg:block lg:flex-1 lg:min-w-0`}
`;

const DeleteListingButton = styled.button`
  ${tw`text-red-600 text-sm font-medium mt-3 flex items-center gap-1.5 transition-colors bg-transparent border-none p-0 cursor-pointer`}
  &:hover {
    ${tw`text-red-700`}
  }
`;

const ReactivateButton = styled.button`
  ${tw`text-green-700 text-sm font-medium mt-3 flex items-center gap-1.5 transition-colors bg-transparent border-none p-0 cursor-pointer`}
  &:hover {
    ${tw`text-green-800`}
  }
`;

const StatusPanel = styled.div`
  ${tw`mt-2 p-4 rounded-lg`}
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
`;

const StatusLine = styled.div`
  ${tw`flex items-center gap-2 text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const StatusDot = styled.span<{ $on: boolean }>`
  ${tw`w-2 h-2 rounded-full inline-block`}
  background-color: ${({ $on }) => ($on ? 'var(--color-success)' : 'var(--color-error)')};
`;

const StatusCopy = styled.p`
  ${tw`text-sm mt-1`}
  color: var(--color-text-secondary);
`;

export default function ViewCarPage() {
  const { id: carId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { capabilities: hostCapabilities, isAdmin } = useHostCapabilities();
  const { uniformDailyRate } = useBrandRules();
  const sectionView = (section: string) => carSectionView(section, hostCapabilities, isAdmin);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get section from URL params, or default to dailyPricing
  const sectionParam = searchParams.get('section');
  const [activeSection, setActiveSection] = useState(sectionParam || 'dailyPricing');

  // Sync activeSection with URL params
  useEffect(() => {
    if (sectionParam && HIDDEN_HOST_CAR_SECTIONS.has(sectionParam)) {
      setActiveSection('dailyPricing');
      setSearchParams({ section: 'dailyPricing' });
      return;
    }
    if (sectionParam && sectionParam !== activeSection) {
      setActiveSection(sectionParam);
    } else if (!sectionParam && activeSection !== 'dailyPricing') {
      // If no section param and not default, add it to URL
      setSearchParams({ section: activeSection });
    }
  }, [sectionParam]); // Only depend on sectionParam to avoid infinite loops

  const listingRefetch = [
    { query: GET_CAR, variables: { id: carId } },
    { query: CAR_LISTING_STATUS, variables: { carId } },
  ];
  const [updateCar] = useMutation(UPDATE_CAR, { refetchQueries: listingRefetch });
  const [adminUnlistCar, { loading: unlisting }] = useMutation(ADMIN_UNLIST_CAR, {
    refetchQueries: listingRefetch,
  });
  const [unlistOpen, setUnlistOpen] = useState(false);
  const [unlistNote, setUnlistNote] = useState('');
  const brandName = getBrand().name;

  // Spec 015: why the car is unlisted and who may relist it (owner and managing admins only).
  const { data: listingData } = useApolloQuery<ICarListingStatusQuery>(CAR_LISTING_STATUS, {
    variables: { carId },
    skip: !carId,
    fetchPolicy: 'cache-and-network',
  });
  const listingStatus = listingData?.carListingStatus;

  const handleAdminUnlist = async () => {
    if (!carId || !unlistNote.trim()) return;
    try {
      await adminUnlistCar({ variables: { carId, note: unlistNote.trim() } });
      toast.success('Car unlisted. The host was emailed.');
      setUnlistOpen(false);
      setUnlistNote('');
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'Could not unlist the car'));
    }
  };

  const handleDeleteListing = () => {
    if (!carId) return;

    toastActions.showToastAlert(
      'Delete Listing',
      'Are you sure you want to deactivate this listing? Your car will no longer appear in search results.',
      'This action can be reversed by reactivating the listing.',
      () => {
        updateCar({
          variables: {
            id: carId,
            updateCarInput: {
              status: CarStatus.Deactivated,
            },
          },
          onCompleted: () => {
            toast.success('Listing deactivated successfully');
          },
          onError: error => {
            toast.error(graphqlErrorMessage(error, 'Error deactivating listing'));
          },
        });
      },
      () => {
        // Cancel button callback - do nothing
      },
      'Deactivate',
      'Cancel',
    );
  };

  const handleReactivateListing = () => {
    if (!carId) return;

    toastActions.showToastAlert(
      'Reactivate Listing',
      'Are you sure you want to reactivate this listing? Your car will appear in search results again.',
      '',
      () => {
        updateCar({
          variables: {
            id: carId,
            updateCarInput: {
              status: CarStatus.Available,
            },
          },
          onCompleted: () => {
            toast.success('Listing reactivated successfully');
          },
          onError: error => {
            // Spec 015: CAR_RELIST_REQUIRES_ADMIN carries who to contact.
            toast.error(graphqlErrorMessage(error, 'Error reactivating listing'));
          },
        });
      },
      () => {
        // Cancel button callback - do nothing
      },
      'Reactivate',
      'Cancel',
    );
  };

  const handleNavClick = (section: string) => {
    setActiveSection(section);
    // Update URL params to persist section
    setSearchParams({ section });
    if (window.innerWidth < 1024) {
      setIsModalOpen(true);
    }
  };

  const renderRightColumnContent = () => {
    const setBy = CAR_SECTION_CAPABILITY[activeSection];
    const view = sectionView(activeSection);
    if (setBy && view === 'hidden') {
      return <ManagedByBrandNote capability={setBy} />;
    }
    const content = renderSection();
    if (setBy && view === 'adminOnly') {
      return (
        <>
          <AdminOnlyNote capability={setBy} />
          {content}
        </>
      );
    }
    return content;
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'dailyPricing':
        return (
          <PriceCalendar
            sectionTitle="Daily Pricing Calendar"
            modeLabel="daily"
            priceCalendar={data?.car?.priceCalendar || []}
            carId={carId || ''}
            weekdayPrice={data?.car?.weekdayPrice}
            weekendPrice={data?.car?.weekendPrice}
            holidayPrice={data?.car?.holidayPrice}
            timezone={data?.car?.location?.timezone || 'America/Los_Angeles'}
            bookings={data?.car?.bookings || []}
            unavailabilities={data?.car?.availabilities || []}
            syncUrlOnChange
            // Spec 014b FR-018: the brand uses one daily rate for every day.
            pricingLocked={uniformDailyRate}
          />
        );
      case 'hourlyPricing':
        return (
          <PriceCalendar
            sectionTitle="Hourly Pricing Calendar"
            modeLabel="hourly"
            priceCalendar={data?.car?.priceCalendar || []}
            carId={carId || ''}
            hourlyRentalIsOn={data?.car?.hourlyRentalIsOn || false}
            weekdayHrPrice={data?.car?.weekdayHrPrice}
            weekendHrPrice={data?.car?.weekendHrPrice}
            holidayHrPrice={data?.car?.holidayHrPrice}
            timezone={data?.car?.location?.timezone || 'America/Los_Angeles'}
            bookings={data?.car?.bookings || []}
            unavailabilities={data?.car?.availabilities || []}
            syncUrlOnChange
          />
        );
      case 'monthlyPricing':
        return (
          <MonthlyPricingSection
            carId={carId || ''}
            monthlyRentalIsOn={data?.car?.monthlyRentalIsOn ?? false}
            monthlyPrice={data?.car?.monthlyPrice ?? null}
            monthlyPrice2Months={data?.car?.monthlyPrice2Months ?? null}
            monthlyPrice3Months={data?.car?.monthlyPrice3Months ?? null}
            distanceLimit={data?.car?.distanceLimit ?? null}
            maximumRentalDays={data?.car?.maximumRentalDays ?? null}
          />
        );
      case 'discounts':
        return <Discount discount={data?.car?.discount || ({} as IDiscount)} carId={carId || ''} />;
      case 'protectionPlan':
        return <CarProtectionDeclinable carId={carId || ''} />;
      case 'tripPreferences':
        return (
          <TripPreferences
            carId={carId || ''}
            tripPreferences={data?.car?.tripPreferences || null}
          />
        );
      case 'distanceLimit':
        return (
          <DistanceLimits carId={carId || ''} distanceLimit={data?.car?.distanceLimit || null} />
        );
      case 'depositAmount':
        return <DepositAmount carId={carId || ''} deposit={data?.car?.deposit ?? undefined} />;
      case 'photos':
        return (
          <EditCarPhotos
            photos={(data?.car?.images || []).map(img => img.imageUrl)}
            carId={carId || ''}
          />
        );

      case 'ageLimit':
        return <AgeLimit ageLimit={data?.car?.ageLimit || 18} carId={carId || ''} />;

      case 'details':
        return <CarDetailsComponent car={data?.car} />;
      case 'location':
        return (
          <CarLocation
            carId={carId || ''}
            latitude={data?.car?.latitude}
            longitude={data?.car?.longitude}
          />
        );
      case 'deliveryLocations':
        return <DeliveryLocations carId={carId || ''} />;
      case 'guestInstructions':
        return (
          <GuestInstructions carId={carId || ''} guestInstructions={data?.car?.guestInstructions} />
        );
      case 'tripHistory':
        return <CarTripHistory carId={carId || ''} />;
      default:
        return <div>todo</div>;
    }
  };

  const { data, loading, error } = useApolloQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
  });
  if (loading) return <LoadingSpinner fullScreen />;

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const car = data?.car;

  if (!car) {
    return <div>something went wrong</div>;
  }

  // `type` was recently added to the backend schema; cast is used to avoid
  // mismatches with stale generated types until codegen is rerun.
  const carType = car?.productTypeDetails?.type as ProductType | undefined;
  const isCarType = !carType || carType === ProductType.Car;

  return (
    <PageContainer>
      <LeftColumn>
        <CarPreview>
          <CarImage>
            <img src={`${URLS.S3_BUCKET.CAR_THUMBNAILS}/${car.thumbnailUrl}`} />
            <ViewListingButton
              onClick={() => {
                // Navigate to guest car view page for this car
                if (carId) {
                  navigate(`/cars/guest/view/${carId}`);
                }
              }}
            >
              View listing
            </ViewListingButton>
          </CarImage>

          <CarInfo>
            <CarTitle>
              {isCarType
                ? `${car.make ?? ''} ${car.model ?? ''} ${car.year ?? ''}`.trim() || car.name
                : car.displayName ||
                  `${car.make ?? ''} ${car.model ?? ''} ${car.year ?? ''}`.trim() ||
                  car.name}
            </CarTitle>
            <CarDetails>
              {isCarType
                ? `#${car.vin || 'N/A'}`
                : `model: ${car.model ?? ''}, make: ${car.make ?? ''}, year: ${car.year ?? ''}`}
            </CarDetails>
            <CarDetails>Trips: 50</CarDetails>
          </CarInfo>
        </CarPreview>

        <NavigationList>
          {HOST_CAR_NAV.filter(([section]) => sectionView(section) !== 'hidden').map(
            ([section, label]) => (
              <NavItem
                key={section}
                isActive={activeSection === section}
                onClick={() => handleNavClick(section)}
              >
                {label}
                {sectionView(section) === 'adminOnly' && (
                  <>
                    {' '}
                    <AdminOnlyBadge testId={`admin-only-section-${section}`} />
                  </>
                )}
              </NavItem>
            ),
          )}
        </NavigationList>

        <StatusPanel data-testid="listing-status-panel">
          {(() => {
            const hold = listingStatus?.hold ?? null;
            const unlisted =
              Boolean(hold) ||
              car.status === CarStatus.Deactivated ||
              car.status === CarStatus.Sold;
            // Without a hold (a car unlisted before holds existed) the owner relists as before.
            const canRelist = hold ? Boolean(listingStatus?.canHostRelist) : unlisted;
            if (!unlisted) {
              return (
                <>
                  <StatusLine>
                    <StatusDot $on />
                    Listed
                  </StatusLine>
                  <StatusCopy>Your car appears in search results and can be booked.</StatusCopy>
                  <DeleteListingButton type="button" onClick={handleDeleteListing}>
                    Delete listing
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </DeleteListingButton>
                  {listingStatus?.isAdminView && (
                    <DeleteListingButton
                      type="button"
                      data-testid="admin-unlist-button"
                      onClick={() => setUnlistOpen(true)}
                    >
                      Unlist (admin)
                    </DeleteListingButton>
                  )}
                </>
              );
            }
            return (
              <>
                <StatusLine>
                  <StatusDot $on={false} />
                  {car.status === CarStatus.Sold && !hold ? 'Sold' : 'Unlisted'}
                </StatusLine>
                {hold && listingStatus ? (
                  <ListingHoldBanner status={listingStatus} hold={hold} brandName={brandName} />
                ) : (
                  <StatusCopy>
                    Your car is hidden from search results and cannot be booked.
                  </StatusCopy>
                )}
                {canRelist && (
                  <ReactivateButton
                    type="button"
                    data-testid="reactivate-listing-button"
                    onClick={handleReactivateListing}
                  >
                    Reactivate listing
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </ReactivateButton>
                )}
                {hold && listingStatus?.isAdminView && (
                  <ReactivateButton
                    type="button"
                    data-testid="admin-review-unlisted-link"
                    onClick={() =>
                      navigate(
                        `/admin/unlisted-cars?search=${encodeURIComponent(car.vin || carId || '')}`,
                      )
                    }
                  >
                    Review in Unlisted cars
                  </ReactivateButton>
                )}
              </>
            );
          })()}
        </StatusPanel>
      </LeftColumn>

      <RightColumn>{renderRightColumnContent()}</RightColumn>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {renderRightColumnContent()}
      </Modal>

      <Modal isOpen={unlistOpen} onClose={() => setUnlistOpen(false)} title="Unlist this car">
        <StatusLine>Unlist this car</StatusLine>
        <StatusCopy>
          Guests can no longer find or book it. The host is emailed with your note, and only an
          admin can relist it. Booked trips stay; the host is asked to swap or cancel them.
        </StatusCopy>
        <Field label="Note for the host" htmlFor="admin-unlist-note" required>
          <Textarea
            id="admin-unlist-note"
            data-testid="admin-unlist-note"
            rows={3}
            maxLength={2000}
            value={unlistNote}
            onChange={event => setUnlistNote(event.target.value)}
          />
        </Field>
        <Button
          theme="danger"
          type="button"
          text="Unlist car"
          dataTestId="admin-unlist-submit"
          isDisabled={!unlistNote.trim()}
          isLoading={unlisting}
          onClick={handleAdminUnlist}
          marginTop={12}
        />
      </Modal>
    </PageContainer>
  );
}
